import { proxy, subscribe } from 'valtio';
import { subscribeKey } from 'valtio/utils';

import { LocalStorageService } from 'services/LocalStorageService';

const LOCAL_STORAGE_KEY = 'dcide-project-canvas';

export type CanvasState = {
    grid: boolean;
    signOff: boolean;
    layersPanelOpen: boolean;
    layersPanelHint: boolean;
    labels: {
        // Each suggestion stores the id of the diagram it was triggered for
        suggestions: {
            labels: string | null;
        };
        components: {
            all: boolean;
            productInformation: boolean;
        };
        ports: {
            voltage: boolean;
            current: boolean;
            power: boolean;
            others: boolean;
        };
        connections: {
            showAsLosses: boolean;
            voltage: boolean;
            voltageSuggestion: boolean;
            current: boolean;
            currentSuggestion: boolean;
            wireSize: boolean;
            wireSizingCheck: boolean;
            lines: boolean;
            others: boolean;
            productInformation: boolean;
        };
        simulation: {
            voltage: boolean;
            current: boolean;
            power: boolean;
            stateOfCharge: boolean;
            powerLosses: boolean;
            powerLossesInPercent: boolean;
            touchVoltage: boolean;
        };
    };
    scale: number;
    translation: {
        x: number;
        y: number;
    };
    inViewportChecks: boolean;
    componentInstancesInViewport: string[];
    connectingComponentInstances: string[];
    printing: boolean;
    // Show the coordinate strips around the grid while printing
    printCoordinates: boolean;
    // Show the grid background while printing
    printGrid: boolean;
    printingViewportRecovery: {
        scale: number;
        translation: {
            x: number;
            y: number;
        };
    } | null;
};

const DEFAULT_LABELS: CanvasState['labels'] = {
    suggestions: {
        labels: null,
    },
    components: {
        all: true,
        productInformation: true,
    },
    ports: {
        voltage: true,
        current: true,
        power: true,
        others: true,
    },
    connections: {
        showAsLosses: false,
        voltage: true,
        voltageSuggestion: false,
        current: true,
        currentSuggestion: false,
        wireSize: true,
        wireSizingCheck: false,
        lines: true,
        others: true,
        productInformation: true,
    },
    simulation: {
        voltage: true,
        current: true,
        power: true,
        stateOfCharge: true,
        powerLosses: false,
        powerLossesInPercent: false,
        touchVoltage: true,
    },
};

const canvasState = proxy<CanvasState>({
    grid: true,
    signOff: true,
    layersPanelOpen: false,
    layersPanelHint: false,
    labels: structuredClone(DEFAULT_LABELS),
    scale: 1,
    translation: {
        x: 0,
        y: 0,
    },
    inViewportChecks: true,
    componentInstancesInViewport: [],
    connectingComponentInstances: [],
    printing: false,
    printCoordinates: true,
    printGrid: true,
    printingViewportRecovery: null,
});

const setSignOffLayer = (show: boolean) => {
    canvasState.signOff = show;
};

const openLayersPanel = (hint = false) => {
    canvasState.layersPanelOpen = true;
    canvasState.layersPanelHint = hint;
};

const closeLayersPanel = () => {
    canvasState.layersPanelOpen = false;
    canvasState.layersPanelHint = false;
};

const LABEL_GROUPS = ['components', 'ports', 'connections', 'simulation'] as const;

type LabelGroup = (typeof LABEL_GROUPS)[number];
type LabelFlags = Record<string, boolean>;

const labelGroup = (labels: CanvasState['labels'], group: LabelGroup): LabelFlags => labels[group];

const areLabelsVisible = (labels: CanvasState['labels']): boolean => {
    return LABEL_GROUPS.some((group) => Object.values(labelGroup(labels, group)).some(Boolean));
};

let labelsBeforeHiding: LabelFlags | null = null;

const setLabelsLayer = (show: boolean) => {
    if (!show) {
        const captured: LabelFlags = {};

        for (const group of LABEL_GROUPS) {
            const flags = labelGroup(canvasState.labels, group);

            for (const key of Object.keys(flags)) {
                captured[`${group}.${key}`] = Boolean(flags[key]);
                flags[key] = false;
            }
        }

        labelsBeforeHiding = captured;

        return;
    }

    for (const group of LABEL_GROUPS) {
        const flags = labelGroup(canvasState.labels, group);

        for (const key of Object.keys(flags)) {
            flags[key] = labelsBeforeHiding
                ? Boolean(labelsBeforeHiding[`${group}.${key}`])
                : Boolean(labelGroup(DEFAULT_LABELS, group)[key]);
        }
    }

    labelsBeforeHiding = null;
};

// Layer and label preferences, saved per project. The viewport is saved separately by ViewportService
type StoredCanvas = {
    grid?: boolean;
    signOff?: boolean;
    labels?: Partial<Record<LabelGroup, LabelFlags>>;
};

let persistedProjectId: string | null = null;

const getStoredCanvases = () => {
    return LocalStorageService.get<Record<string, StoredCanvas>>(LOCAL_STORAGE_KEY) ?? {};
};

const persist = () => {
    if (!persistedProjectId) {
        return;
    }

    const stored = getStoredCanvases();

    stored[persistedProjectId] = {
        grid: canvasState.grid,
        signOff: canvasState.signOff,
        labels: Object.fromEntries(LABEL_GROUPS.map((group) => [group, { ...labelGroup(canvasState.labels, group) }])),
    };

    LocalStorageService.store(LOCAL_STORAGE_KEY, stored);
};

subscribe(canvasState.labels, persist);
subscribeKey(canvasState, 'grid', persist);
subscribeKey(canvasState, 'signOff', persist);

const initCanvas = (projectId: string) => {
    const stored = getStoredCanvases()[projectId];

    canvasState.grid = stored?.grid ?? true;
    canvasState.signOff = stored?.signOff ?? true;

    // Fall back to defaults per flag, so flags added after the project was stored still get a value
    for (const group of LABEL_GROUPS) {
        const flags = labelGroup(canvasState.labels, group);
        const defaults = labelGroup(DEFAULT_LABELS, group);

        for (const key of Object.keys(defaults)) {
            const value = stored?.labels?.[group]?.[key];

            flags[key] = typeof value === 'boolean' ? value : defaults[key];
        }
    }

    labelsBeforeHiding = null;
    persistedProjectId = projectId;
};

export {
    areLabelsVisible,
    canvasState,
    closeLayersPanel,
    DEFAULT_LABELS,
    initCanvas,
    openLayersPanel,
    setLabelsLayer,
    setSignOffLayer,
};
